import { Box } from "@chakra-ui/react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SearchBarV2 } from "./SearchBarV2";
import { SearchDropdown } from "./dropdown/SearchDropdown";
import { useSearchV2, BROWSE_SEGMENTS } from "../hooks/useSearchContext";
import { isChannelContext } from "../types";
import type { SearchQueryResponse } from "../types";
import { useInstantSearch } from "../hooks/useInstantSearch";

/**
 * When context is "all", pick the best results tab:
 * series first, then releases, then creators.
 */
function pickBestPath(
  query: string,
  results: SearchQueryResponse | undefined,
): string {
  const q = encodeURIComponent(query);
  if (!results) return `/browse?q=${q}`;

  if (results.series.total > 0) return `/browse?q=${q}`;
  if (results.releases.total > 0) return `/books?q=${q}`;
  if (results.creators.total > 0) return `/creators?q=${q}`;

  // Nothing found — default to series tab
  return `/browse?q=${q}`;
}

export function SearchBarWithDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { context, commitSearch } = useSearchV2();
  // No dropdown on browse/results pages — the search bar drives the page directly
  const isBrowsePage = BROWSE_SEGMENTS.has(location.pathname.split("/")[1] ?? "");

  // Derive CMS context key from search context
  const isChannel = isChannelContext(context);
  const isAllContext = !isChannel && context === "all";
  const cmsContext = isChannel ? "all" : context;

  // Standard context for API calls (undefined for channels — filters handle scoping)
  const searchContext = isChannel ? undefined : (context === "all" ? undefined : context);

  // Derive channel filters for suggest/instant search
  const channelFilters = isChannel ? context.filters : undefined;

  // Instant search results — used to pick the best tab when context is "all"
  const { data: instantResults } = useInstantSearch(
    inputValue.trim(),
    searchContext,
    channelFilters,
  );

  // Close on location change
  const locationKey = location.pathname + location.search;
  useEffect(() => {
    setIsOpen(false);
  }, [locationKey]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const handleFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      setIsOpen(false);
      navigate(path);
    },
    [navigate],
  );

  const commitAndClose = useCallback(
    (query: string) => {
      setIsOpen(false);

      if (isAllContext) {
        // Pick the best tab based on instant search results
        navigate(pickBestPath(query, instantResults));
      } else {
        commitSearch(query);
      }
    },
    [commitSearch, isAllContext, instantResults, navigate],
  );


  const handleSelectSuggestion = useCallback(
    (name: string) => {
      setInputValue(name);
      commitAndClose(name);
    },
    [commitAndClose],
  );

  return (
    <Box ref={wrapperRef} position="relative" w="full">
      <SearchBarV2
        onFocus={handleFocus}
        onChange={handleChange}
      />
      {isOpen && !isBrowsePage && (
        <SearchDropdown
          inputValue={inputValue}
          cmsContext={cmsContext}
          context={searchContext}
          channelFilters={channelFilters}
          onNavigate={handleNavigate}
          onSelectSuggestion={handleSelectSuggestion}
        />
      )}
    </Box>
  );
}
